import { SettingOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Select } from 'antd';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { CenterFlex, Divider, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import LazyLoader from '@/components/Common/LazyLoader';
import ScrollView from '@/components/Common/ScrollView';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import SearchInput from '@/components/Inputs/SearchInput';
import { useModalContext } from '@/components/Modal/Modal';
import type { Tab } from '@/components/Tab/types';
import useTabs from '@/components/Tab/useTabs';
import Typo from '@/components/Typography/Typo';
import { useChartListQuery } from '@/features/(charts)/chart-list/hooks/use-chart-list-query';
import ChartSelectItem from '@/features/(charts)/chart-list/ui/chart-select-item';
import { Dashboard2ColumnType } from '@/features/Dashboard/types/constants';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import service from '@/services/index';
import usePopupStore from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';
import useUserStore from '@/stores/UserStore';
import { radius } from '@/styles/sizes';
import { modalStyle } from '@/styles/style';
import { lowerCaseMatch } from '@/utils/util';

type Props = {
  dashboardId: string;
  rowIndex?: number;
  currentCharts?: string[];
};

const AddChartFromSavedChartModal = (props: Props) => {
  const color = useColor();
  const addToast = useToast();
  const { setPopup } = usePopupStore((state) => ({ setPopup: state.setPopup }));
  const { closeModal } = useModalContext();
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedChartList, setSelectedChartList] = useState<string[]>([]);
  const wr = useWorkspaceRouter();

  const selectChart = (alias: string) => {
    setSelectedChartList((prev) => prev.toggle(alias));
  };
  const clearSearchInput = () => setSearchInput('');
  const { data: chartList, isLoading: isLoadingChartList } =
    useChartListQuery();
  const user = useUserStore((state) => state.user);
  const queryClient = useQueryClient();

  const tabs: Tab[] = useMemo(() => {
    let myCount = 0;
    let starredCount = 0;

    chartList?.forEach((row) => {
      if (row.updated_by === user?.user_id) {
        myCount += 1;
      }
      if (row.starred) {
        starredCount += 1;
      }
    });
    return [
      {
        text: `All (${chartList?.length})`,
        id: 'all',
      },
      {
        text: `My charts (${myCount})`,
        id: 'my',
      },
      {
        text: `Starred charts (${starredCount})`,
        id: 'starred',
      },
    ];
  }, [chartList, user?.user_id]);
  const [selectedTab, selectTab] = useTabs(tabs, 'tab');

  const onSelectTab = (tabId: StringNumberValue | null) => {
    selectTab(tabId);
  };

  const selectOptions = tabs.map((tab) => ({
    value: tab.id,
    label: tab.text,
  }));

  const filteredData = useMemo(() => {
    let filteredChartList: ChartItem[] = [];

    if (selectedTab === 'starred')
      filteredChartList = chartList?.filter((item) => item.starred) || [];
    if (selectedTab === 'my')
      filteredChartList =
        chartList?.filter((item) => item.updated_by === user?.user_id) || [];
    if (selectedTab === 'all') filteredChartList = chartList || [];

    if (searchInput) {
      filteredChartList = filteredChartList.filter((item) =>
        lowerCaseMatch(item.name, searchInput),
      );
    }

    return filteredChartList;
  }, [selectedTab, chartList, searchInput, user?.user_id]);

  const addCharts = async () => {
    for (const alias of selectedChartList) {
      if (props.currentCharts?.includes(alias)) {
        const chart = chartList?.find((c) => c.alias === alias);
        setPopup({
          popupTheme: 'negative',
          title: 'Error',
          message: `${chart?.name} is already exist in this dashboard`,
          type: 'alert',
        });
        return;
      }
    }
    try {
      if (typeof props.rowIndex === 'number') {
        await service.dashboard2.addDashboardRow(props.dashboardId, {
          pos: props.rowIndex,
          columns: selectedChartList.map((alias) => ({
            type: Dashboard2ColumnType.DASHBOARD_COLUMN_CHART,
            chart: alias,
          })),
        });
      } else {
        await service.dashboard2.addMultipleChartInDashboard(
          props.dashboardId,
          selectedChartList,
        );
      }

      // refetch dashboard
      queryClient.invalidateQueries({ queryKey: ['dashboard2', props.dashboardId] });

      closeModal();

      addToast('Chart added', 'success');
    } catch (e) {
      setPopup({
        popupTheme: 'negative',
        title: 'Error',
        message: 'Failed to add the item.',
        type: 'alert',
      });
    }
  };

  return (
    <StyledAddChartFromSavedChartModal>
      <VFlex padding="24px 24px 0px 24px">
        <Flex j="space-between" a="center">
          <Typo type="label0" weight="sb" color={color.gray800}>
            Saved chart
          </Typo>
          <Button
            variant="ghost"
            sizeType="m"
            onClick={() => wr.push('/settings/charts')}
            leftIcon={<SettingOutlined />}
          >
            Manage
          </Button>
        </Flex>
        <Space h="40px" />

        <Transition
          status={!isLoadingChartList}
          fallback={
            <LazyLoader
              show={isLoadingChartList}
              delay={500}
              loader={
                <CenterFlex height="300px">
                  <Spinner />
                </CenterFlex>
              }
            />
          }
        >
          <Flex j="space-between" a="center">
            <Select
              style={{ width: 140 }}
              options={selectOptions}
              value={selectedTab}
              onSelect={onSelectTab}
              getPopupContainer={() =>
                document.getElementById('dropdown-layer') || document.body
              }
            />
            <SearchInput
              placeholder="Search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onClickClear={clearSearchInput}
            />
          </Flex>
        </Transition>

        <Space h="24px" />

        <ScrollView
          style={{
            maxHeight: '400px',
          }}
        >
          <VFlex g={8}>
            {filteredData?.map((chart) => (
              <ChartSelectItem
                key={chart.alias}
                chart={chart}
                onClick={selectChart}
                disabled={props.currentCharts?.includes(chart.alias)}
                selected={selectedChartList.includes(chart.alias)}
              />
            ))}
          </VFlex>
        </ScrollView>
      </VFlex>
      <Divider />
      <Flex padding="24px" j="space-between" a="center">
        <Typo type="label1" className="text-primary">
          {selectedChartList.length} selected
        </Typo>
        <Flex g={12}>
          <Button variant="ghost" sizeType="l" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" sizeType="l" onClick={addCharts}>
            Add
          </Button>
        </Flex>
      </Flex>
    </StyledAddChartFromSavedChartModal>
  );
};

export default AddChartFromSavedChartModal;

const StyledAddChartFromSavedChartModal = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 800px;
  background-color: ${(props) => props.theme.colors.gray100};
  border-radius: ${radius.token['2xl']}px;
  padding: 0;
  ${modalStyle};
  .text-primary {
    color: ${(props) => props.theme.colors.primary100};
  }
`;
